import { Component } from '@theme/component';

/**
 * PDP fullscreen lightbox (design .pdp-lightbox): single image/video stage,
 * prev/next arrows, close button. Bespoke component (not a Horizon zoom-dialog
 * fork - that component is architecturally a scrollable multi-image gallery,
 * not a single-slide-with-arrows viewer). Syncs the main gallery's
 * slideshow-component on open/prev/next so the two stay in step.
 *
 * A plain fixed-position overlay, not a native <dialog>/showModal - matches
 * the static design's own vanilla implementation and avoids relying on
 * top-layer promotion for a full-bleed backdrop.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} dialog
 * @property {HTMLElement[]} slides
 *
 * @extends Component<Refs>
 */
export class MtcLightbox extends Component {
  requiredRefs = ['dialog', 'slides'];

  #index = 0;
  /** @type {HTMLElement | null} */
  #trigger = null;
  #reparented = false;
  /** @type {Element | null} */
  #owningSection = null;
  /** @type {HTMLElement | null} */
  #scroller = null;
  #previousScrollerOverflow = '';

  connectedCallback() {
    super.connectedCallback();
    // Reparent to <body> so the fixed overlay can't be clipped or stacked
    // under an ancestor (e.g. a sticky/contained media-gallery column).
    if (this.parentElement !== document.body) {
      // Held so the unload handler can tell this instance's own section from any other.
      this.#owningSection = this.closest('.shopify-section');
      // A shopify:section:load re-render renders a fresh instance with the same id while
      // the previous one still lives under <body>, outside the replaced subtree. Drop the
      // old one, or the gallery's id-based on:click would resolve to stale markup.
      for (const previous of document.querySelectorAll('mtc-lightbox')) {
        if (previous !== this && previous.id === this.id) previous.remove();
      }
      this.#reparented = true;
      document.body.appendChild(this);
      return;
    }
    this.refs.dialog.addEventListener('keydown', this.handleKeyDown);
    this.refs.dialog.addEventListener('click', this.handleBackdropClick);
    document.addEventListener('shopify:section:unload', this.handleSectionUnload);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.dialog?.removeEventListener('keydown', this.handleKeyDown);
    this.refs.dialog?.removeEventListener('click', this.handleBackdropClick);
    document.removeEventListener('shopify:section:unload', this.handleSectionUnload);
    // Removal can happen while the overlay is still open - by the duplicate cleanup above,
    // or by the unload handler - and neither runs close(), so release the lock here too.
    this.#releaseScrollLock();
  }

  /**
   * Opens the lightbox at the given slide index.
   * @param {number} index
   * @param {Event} [event]
   */
  openAt(index, event) {
    event?.preventDefault();
    this.#trigger = event?.target instanceof HTMLElement ? event.target : null;
    this.#show(Number(index));
    this.refs.dialog.classList.add('is-open');
    this.refs.dialog.setAttribute('aria-hidden', 'false');
    // Horizon scrolls .page-wrapper - html and body are already overflow:hidden, so
    // locking documentElement leaves the real scroller free to move behind the overlay.
    if (!this.#scroller) {
      this.#scroller = document.querySelector('.page-wrapper');
      this.#previousScrollerOverflow = this.#scroller?.style.overflow ?? '';
      if (this.#scroller) this.#scroller.style.overflow = 'hidden';
    }
    this.refs.dialog.focus();
  }

  close() {
    this.refs.dialog.classList.remove('is-open');
    this.refs.dialog.setAttribute('aria-hidden', 'true');
    this.#releaseScrollLock();
    this.#trigger?.focus?.();
  }

  /** @param {Event} [event] */
  prev(event) {
    event?.preventDefault();
    const total = this.refs.slides.length;
    this.#show((this.#index - 1 + total) % total);
  }

  /** @param {Event} [event] */
  next(event) {
    event?.preventDefault();
    const total = this.refs.slides.length;
    this.#show((this.#index + 1) % total);
  }

  handleKeyDown = (/** @type {KeyboardEvent} */ event) => {
    if (!this.refs.dialog.classList.contains('is-open')) return;
    if (event.key === 'Escape') this.close();
    else if (event.key === 'ArrowLeft') this.prev();
    else if (event.key === 'ArrowRight') this.next();
    else if (event.key === 'Tab') {
      // aria-modal does not constrain focus, so Tab would otherwise reach the page behind.
      const focusables = this.#focusables();
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;
      if (event.shiftKey && (active === first || active === this.refs.dialog)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    }
  };

  handleBackdropClick = (/** @type {MouseEvent} */ event) => {
    if (event.target === this.refs.dialog) this.close();
  };

  handleSectionUnload = (/** @type {Event} */ event) => {
    // The overlay lives under <body>, so an unload of its section never disconnects it.
    if (this.#owningSection && event.target === this.#owningSection) this.remove();
  };

  #releaseScrollLock() {
    if (!this.#scroller) return;
    this.#scroller.style.overflow = this.#previousScrollerOverflow;
    this.#scroller = null;
    this.#previousScrollerOverflow = '';
  }

  #focusables() {
    return /** @type {HTMLElement[]} */ ([
      ...this.refs.dialog.querySelectorAll('button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])'),
    ]).filter((element) => element.offsetParent !== null);
  }

  /** @param {number} index */
  #show(index) {
    this.#index = index;
    this.refs.slides.forEach((slide, i) => {
      slide.hidden = i !== index;
    });
    this.#syncMainSlideshow(index);
  }

  /** @param {number} index */
  #syncMainSlideshow(index) {
    // Looked up live rather than cached at connect time: this element is reparented
    // to <body> on first connect (see connectedCallback), so it sits outside the
    // gallery's own subtree and would keep a stale reference across a
    // shopify:section:load re-render of the gallery in the theme editor.
    const blockId = this.id.replace(/^mtc-lightbox-/, '');
    const gallery = document.querySelector(`media-gallery[data-block-id="${CSS.escape(blockId)}"]`);
    const slideshow = /** @type {import('./slideshow').Slideshow | null} */ (
      gallery?.querySelector('slideshow-component')
    );
    slideshow?.select?.(index, undefined, { animate: false });
  }
}

if (!customElements.get('mtc-lightbox')) {
  customElements.define('mtc-lightbox', MtcLightbox);
}
